import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { PropertiesData } from "@/types/tree";
import { documentStore } from "@/store/store";
import { SsrNode, SsrNodeV } from "@/types/server-side-render";
import { Either } from "fp-ts/lib/Either";
import { _Error } from "@/lib/wordpress/data";
import { getFrontendUrl } from "@/lib/document";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { treeNodesOfType } from "@/lib/tree/tree";

const debounceTimeoutTable: {
  [key: number]: NodeJS.Timeout;
} = {};

/*
 * Works the same as a debounce function, but it return a promise that will execute its function and be resolved
 * after "interval" of not being called.
 *
 * It uses a timeoutTable to be able to debounce different calls at the same time.
 */
function promiseDebounce(
  exec: (...args: any[]) => Promise<Either<_Error[], SsrNode>>,
  interval: number,
  id: number
): () => ReturnType<typeof exec> {
  let resolves: Array<(value: Promise<Either<_Error[], SsrNode>>) => void> = [];

  return async (...args: unknown[]) => {
    if (debounceTimeoutTable[id]) {
      clearTimeout(debounceTimeoutTable[id]);
    }

    debounceTimeoutTable[id] = setTimeout(() => {
      const result = exec(...args);
      resolves.forEach(resolve => resolve(result));
      resolves = [];

      delete debounceTimeoutTable[id];
    }, interval);

    return new Promise(resolve => resolves.push(resolve));
  };
}

export function serverSideRender(
  elementSlug: string,
  properties: PropertiesData,
  id: number,
  parentProperties: PropertiesData
): Promise<Either<_Error[], SsrNode>> {
  const matchingNodes = treeNodesOfType(documentStore.document.tree.root, [
    elementSlug
  ]);
  const totalMatchingElements = matchingNodes.length;
  const currentIndexInMatchingElements = matchingNodes.findIndex(
    node => node.id === id
  );

  const makeWordPressAjaxRequestFunction = () =>
    makeWordPressAjaxRequest({
      action: "breakdance_server_side_render",
      overrideAjaxUrl: getFrontendUrl(),
      dataToPost: {
        properties: fpJsonStringify(properties),
        parentProperties: fpJsonStringify(parentProperties),
        elementSlug,
        triggeringDocument: documentStore.document.documentMeta.id,
        totalMatchingElements,
        currentIndexInMatchingElements
      },
      decoder: SsrNodeV
    });

  return promiseDebounce(makeWordPressAjaxRequestFunction, 1000, id)();
}
